import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  CalendarDays,
  Landmark,
  Droplets,
  Zap,
  Leaf,
  Play,
  Clock,
  MapPin,
  AlertCircle,
  ChevronRight,
  BookOpen,
  Newspaper,
  Loader2,
} from "lucide-react";
import Topbar from "Components/Topbar/Topbar";
import { useVeilleContext } from "../../contexts/VeilleContext";
import type { VeilleMainTab } from "../../contexts/VeilleContext";
import type {
  VeilleTheme,
  VeilleEvenementiellleResponse,
  VeilleReglementaireResponse,
  VeilleInformatiqueResponse,
} from "../../hooks/veille";
import type { User as BackendUser } from "../../types/user";
import "./VeilleRSEPage.css";

interface VeilleRSEPageProps {
  user: BackendUser;
}

type MainTab = VeilleMainTab;

const MAIN_TABS = [
  {
    id: "evenementielle" as MainTab,
    label: "Veille Événementielle",
    icon: <CalendarDays size={18} />,
    color: "#7c3aed",
    lightColor: "#ede9fe",
  },
  {
    id: "reglementaire" as MainTab,
    label: "Veille Réglementaire",
    icon: <Landmark size={18} />,
    color: "#0891b2",
    lightColor: "#e0f2fe",
  },
  {
    id: "informative" as MainTab,
    label: "Veille Informative",
    icon: <Newspaper size={18} />,
    color: "#16a34a",
    lightColor: "#dcfce7",
  },
];

const INFO_TABS: Array<{
  id: VeilleTheme;
  label: string;
  icon: React.ReactNode;
  color: string;
  lightColor: string;
}> = [
  {
    id: "Eau",
    label: "Eau",
    icon: <Droplets size={16} />,
    color: "#0369a1",
    lightColor: "#dbeafe",
  },
  {
    id: "Energie",
    label: "Énergie",
    icon: <Zap size={16} />,
    color: "#d97706",
    lightColor: "#fef3c7",
  },
  {
    id: "Environnement",
    label: "Environnement",
    icon: <Leaf size={16} />,
    color: "#16a34a",
    lightColor: "#dcfce7",
  },
];

const PERIOD_OPTIONS = [
  { value: 3, label: "3 derniers jours" },
  { value: 7, label: "7 derniers jours" },
  { value: 14, label: "14 derniers jours" },
  { value: 30, label: "30 derniers jours" },
  { value: 60, label: "60 derniers jours" },
  { value: 90, label: "90 derniers jours" },
];

function formatDate(dateStr: string): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const VeilleRSEPage: React.FC<VeilleRSEPageProps> = ({ user }) => {
  const navigate = useNavigate();
  const {
    activeTab,
    setActiveTab,
    activeInfoTab,
    setActiveInfoTab,
    periodDaysEvenementielle,
    setPeriodDaysEvenementielle,
    periodDaysReglementaire,
    setPeriodDaysReglementaire,
    periodDaysInformative,
    setPeriodDaysInformative,
    evenementielle,
    reglementaire,
    informatique,
  } = useVeilleContext();

  const periodDays =
    activeTab === "evenementielle"
      ? periodDaysEvenementielle
      : activeTab === "reglementaire"
        ? periodDaysReglementaire
        : periodDaysInformative;

  const setPeriodDays = (days: number) => {
    if (activeTab === "evenementielle") setPeriodDaysEvenementielle(days);
    else if (activeTab === "reglementaire") setPeriodDaysReglementaire(days);
    else setPeriodDaysInformative(days);
  };

  const handleLancer = () => {
    if (activeTab === "evenementielle") {
      evenementielle.mutate({ period_days: periodDaysEvenementielle });
    } else if (activeTab === "reglementaire") {
      reglementaire.mutate({ period_days: periodDaysReglementaire });
    } else {
      informatique.mutate({
        period_days: periodDaysInformative,
        themes: ["Eau", "Energie", "Environnement"],
      });
    }
  };

  const activeTabMeta = MAIN_TABS.find((t) => t.id === activeTab)!;

  const isPending =
    (activeTab === "evenementielle" && evenementielle.isPending) ||
    (activeTab === "reglementaire" && reglementaire.isPending) ||
    (activeTab === "informative" && informatique.isPending);

  const isError =
    (activeTab === "evenementielle" && evenementielle.isError) ||
    (activeTab === "reglementaire" && reglementaire.isError) ||
    (activeTab === "informative" && informatique.isError);

  const errorMessage =
    (activeTab === "evenementielle" && evenementielle.error?.message) ||
    (activeTab === "reglementaire" && reglementaire.error?.message) ||
    (activeTab === "informative" && informatique.error?.message) ||
    "Une erreur est survenue.";

  const hasData =
    (activeTab === "evenementielle" && !!evenementielle.data) ||
    (activeTab === "reglementaire" && !!reglementaire.data) ||
    (activeTab === "informative" && !!informatique.data);

  return (
    <div className="veille-page">
      <Topbar
        title="Veille RSE"
        userName={user.username}
        onLogout={() => navigate("/")}
        user={user}
      />

      <div className="veille-content">
        {/* Page header */}
        <div className="veille-header">
          <div className="veille-header-icon">
            <Bell size={22} color="#fff" />
          </div>
          <div>
            <h1 className="veille-title">Veille RSE / ESG</h1>
            <p className="veille-subtitle">
              Surveillance automatique des actualités réglementaires,
              événementielles et sectorielles
            </p>
          </div>
        </div>

        {/* Main tabs */}
        <div className="veille-tabs">
          {MAIN_TABS.map((tab) => (
            <button
              key={tab.id}
              className={`veille-tab ${activeTab === tab.id ? "veille-tab--active" : ""}`}
              style={
                activeTab === tab.id
                  ? { borderColor: tab.color, color: tab.color }
                  : {}
              }
              onClick={() => setActiveTab(tab.id)}
            >
              <span
                className="veille-tab-icon"
                style={
                  activeTab === tab.id
                    ? { background: tab.lightColor, color: tab.color }
                    : {}
                }
              >
                {tab.icon}
              </span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Filter & launch bar */}
        <div className="veille-filter-bar">
          <div className="veille-filter-period">
            <Clock size={15} color="#64748b" />
            <span>Période :</span>
            <select
              className="veille-period-select"
              value={periodDays}
              onChange={(e) => setPeriodDays(Number(e.target.value))}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <button
            className="veille-launch-btn"
            style={{ background: activeTabMeta.color }}
            onClick={handleLancer}
            disabled={isPending}
          >
            {isPending ? (
              <Loader2 size={16} className="veille-spin" />
            ) : (
              <Play size={16} />
            )}
            {isPending ? "Analyse en cours..." : "Lancer la veille"}
          </button>
        </div>

        {/* Content panel */}
        <div className="veille-panel">
          {isPending && (
            <div className="veille-loading">
              <Loader2
                size={44}
                className="veille-spin"
                color={activeTabMeta.color}
              />
              <p className="veille-loading-text">
                Collecte et analyse des sources en cours...
              </p>
              <p className="veille-loading-sub">
                Cette opération peut prendre quelques instants.
              </p>
            </div>
          )}

          {!isPending && isError && (
            <div className="veille-error">
              <AlertCircle size={40} color="#ef4444" />
              <p className="veille-error-text">{errorMessage}</p>
              <button className="veille-retry-btn" onClick={handleLancer}>
                Réessayer
              </button>
            </div>
          )}

          {!isPending && !isError && !hasData && (
            <div className="veille-empty">
              <div
                className="veille-empty-icon"
                style={{ background: activeTabMeta.lightColor }}
              >
                <Play size={28} color={activeTabMeta.color} />
              </div>
              <p className="veille-empty-text">
                Lancez la veille pour charger les données
              </p>
              <p className="veille-empty-sub">
                Sélectionnez une période et cliquez sur « Lancer la veille »
              </p>
            </div>
          )}

          {!isPending && !isError && hasData && (
            <>
              {activeTab === "evenementielle" && evenementielle.data && (
                <EvenementiellResults
                  data={evenementielle.data}
                  color={activeTabMeta.color}
                  lightColor={activeTabMeta.lightColor}
                />
              )}
              {activeTab === "reglementaire" && reglementaire.data && (
                <ReglementaireResults
                  data={reglementaire.data}
                  color={activeTabMeta.color}
                  lightColor={activeTabMeta.lightColor}
                />
              )}
              {activeTab === "informative" && informatique.data && (
                <InformativeResults
                  data={informatique.data}
                  activeInfoTab={activeInfoTab}
                  onInfoTabChange={setActiveInfoTab}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Sub-components ────────────────────────────────────────────────────────────

function SyntheseCard({
  text,
  color,
}: {
  text: string;
  color: string;
}) {
  const bullets = text
    .split(/\.(?:\s+|$)/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map((s) => `• ${s}`)
    .join("\n");

  return (
    <div className="veille-synthese">
      <BookOpen size={16} color={color} className="veille-synthese-icon" />
      <p style={{ whiteSpace: "pre-line", margin: 0 }}>{bullets}</p>
    </div>
  );
}

function MetaBar({
  children,
}: {
  children: React.ReactNode;
}) {
  return <div className="veille-section-meta-bar">{children}</div>;
}

function EvenementiellResults({
  data,
  color,
  lightColor,
}: {
  data: VeilleEvenementiellleResponse;
  color: string;
  lightColor: string;
}) {
  return (
    <div>
      {data.synthese && <SyntheseCard text={data.synthese} color={color} />}
      <MetaBar>
        <span>{data.total_evenements} événements détectés</span>
        <span
          className="veille-meta-badge"
          style={{ background: lightColor, color }}
        >
          {data.evenements_futurs} à venir
        </span>
      </MetaBar>
      <div className="veille-articles">
        {data.evenements.map((evt, i) => (
          <div key={i} className="veille-article-card">
            <div className="veille-article-meta">
              <span className="veille-article-date">
                <Clock size={12} />
                {formatDate(evt.date_event)}
              </span>
              <span
                className="veille-article-source"
                style={{ background: lightColor, color }}
              >
                {evt.source}
              </span>
              {evt.est_futur && (
                <span className="veille-badge-futur">À venir</span>
              )}
            </div>
            <h3 className="veille-article-title">{evt.titre}</h3>
            {evt.lieu && (
              <div className="veille-event-lieu">
                <MapPin size={12} />
                {evt.lieu}
              </div>
            )}
            <p className="veille-article-excerpt">{evt.description}</p>
            {evt.url && (
              <a
                href={evt.url}
                target="_blank"
                rel="noopener noreferrer"
                className="veille-article-link"
                style={{ color }}
              >
                Voir l&apos;événement
                <ChevronRight size={14} />
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ReglementaireResults({
  data,
  color,
  lightColor,
}: {
  data: VeilleReglementaireResponse;
  color: string;
  lightColor: string;
}) {
  return (
    <div>
      {data.synthese && <SyntheseCard text={data.synthese} color={color} />}
      <MetaBar>
        <span>{data.total_articles} articles détectés</span>
        <span
          className="veille-meta-badge"
          style={{ background: lightColor, color }}
        >
          {data.articles_dates} datés
        </span>
      </MetaBar>
      <div className="veille-articles">
        {data.articles.map((art, i) => (
          <div key={i} className="veille-article-card">
            <div className="veille-article-meta">
              <span className="veille-article-date">
                <Clock size={12} />
                {formatDate(art.date_publication)}
              </span>
              <span
                className="veille-article-source"
                style={{ background: lightColor, color }}
              >
                {art.source}
              </span>
              {art.score_reg > 0 && (
                <span
                  className="veille-badge-score"
                  style={{ background: lightColor, color }}
                >
                  Score {art.score_reg}
                </span>
              )}
            </div>
            <h3 className="veille-article-title">{art.titre}</h3>
            <p className="veille-article-excerpt">{art.contenu}</p>
            {art.url && (
              <a
                href={art.url}
                target="_blank"
                rel="noopener noreferrer"
                className="veille-article-link"
                style={{ color }}
              >
                Lire l&apos;article
                <ChevronRight size={14} />
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function InformativeResults({
  data,
  activeInfoTab,
  onInfoTabChange,
}: {
  data: VeilleInformatiqueResponse;
  activeInfoTab: VeilleTheme;
  onInfoTabChange: (tab: VeilleTheme) => void;
}) {
  const tab = INFO_TABS.find((t) => t.id === activeInfoTab)!;
  const themeData = data.themes[activeInfoTab];

  return (
    <div>
      {/* Sub-tabs */}
      <div className="veille-info-tabs">
        {INFO_TABS.map((t) => {
          const count = data.themes[t.id]?.count ?? 0;
          const isActive = t.id === activeInfoTab;
          return (
            <button
              key={t.id}
              className={`veille-info-tab ${isActive ? "veille-info-tab--active" : ""}`}
              style={
                isActive
                  ? {
                      background: t.lightColor,
                      borderColor: t.color,
                      color: t.color,
                    }
                  : {}
              }
              onClick={() => onInfoTabChange(t.id)}
            >
              <span style={{ color: isActive ? t.color : "#94a3b8" }}>
                {t.icon}
              </span>
              {t.label}
              <span
                className="veille-info-tab-count"
                style={
                  isActive ? { background: t.color, color: "#fff" } : {}
                }
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {themeData ? (
        <>
          {themeData.synthese && (
            <SyntheseCard text={themeData.synthese} color={tab.color} />
          )}
          <MetaBar>
            <span>{themeData.count} articles détectés</span>
          </MetaBar>
          <div className="veille-articles">
            {themeData.articles.map((art, i) => (
              <div key={i} className="veille-article-card">
                <div className="veille-article-meta">
                  <span className="veille-article-date">
                    <Clock size={12} />
                    {formatDate(art.date_publication)}
                  </span>
                  <span
                    className="veille-article-source"
                    style={{ background: tab.lightColor, color: tab.color }}
                  >
                    {art.source}
                  </span>
                  {art.priorite && (
                    <span
                      className={`veille-badge-priorite veille-badge-priorite--${art.priorite.toLowerCase()}`}
                    >
                      {art.priorite}
                    </span>
                  )}
                </div>
                <h3 className="veille-article-title">{art.titre}</h3>
                <p className="veille-article-excerpt">{art.description}</p>
                {art.url && (
                  <a
                    href={art.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="veille-article-link"
                    style={{ color: tab.color }}
                  >
                    Lire l&apos;article
                    <ChevronRight size={14} />
                  </a>
                )}
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="veille-empty">
          <p className="veille-empty-text">Aucune donnée pour ce thème</p>
        </div>
      )}
    </div>
  );
}

export default VeilleRSEPage;
